// Le Sablier (D-161, D-167), deuxième jeu d'Arceau Game, sur le moteur commun (src/domain/game.ts) et le paquet du Parcours.
// Chaque équipe a sa réserve de temps (8 min par défaut) ; une carte tirée au sort ; le sable de l'équipe coule pendant le
// défi et le retour, jusqu'à son STOP ; certaines cartes rendent du temps, d'autres en prennent ; un raté coûte 20 s ; la
// première équipe à zéro arrête la partie pour tout le monde ; le plus de sable restant gagne. Logique pure, hasard et
// horloge injectés : chaque geste rend le nouvel état et les effets à montrer.
//
// Deux façons de jouer (D-167, choisies à la préparation) :
//  * « Même carte pour tous » (par défaut) : une carte est tirée pour toutes les équipes, qui la font en même temps ; le
//    sable de chacune coule jusqu'à son STOP ; quand toutes ont fini (ou au bout du temps maximum), la carte suivante ;
//  * « Chacune sa carte » (les maquettes) : chaque équipe tire la sienne et enchaîne à son rythme.
// Le temps : tout est en temps de jeu (les pauses ne comptent pas). Le sable d'une équipe = `res` au moment `flowFrom`, puis
// il coule ; `flowFrom` dans le futur = la carte se lit (5 s), le sable attend ; `flowFrom` nul = le sable est arrêté.
// Le zéro se lit à l'heure de la base (le temps vient de l'horloge de la base, D-167) avec 0,5 s de grâce pour un STOP :
// un STOP reçu jusqu'à 0,5 s après le zéro sauve l'équipe (à 0) ; sinon la partie s'arrête à l'instant du zéro, et ce que
// les autres ont fait après cet instant est défait.
// Choix de ce lot :
//  * « Coup de vent » (−15 s) vise l'équipe qui a le plus de sable parmi les autres (jamais celle qui le tire ; « Même carte
//    pour tous » : la ou les premières) ; « Sablier retourné » (+30 s) va à l'équipe qui le tire (« Même carte pour tous » :
//    à toutes) ; les jokers ne sortent pas en « Même carte pour tous » ;
//  * un raté ou une sanction qui vide le sable : l'équipe est à zéro tout de suite (sans grâce) ;
//  * « Même carte pour tous » : « Une autre carte, sans pénalité » n'est pas proposé (un raté gratuit dès la carte lue) ;
//  * changer la carte d'une équipe (coach) : une autre, le sable attend le temps de la lire, sans pénalité.

import {
  act, createGame, gameMs, lastDeclared, lastUndoable, setPaused, shuffled, undoLast,
  type GameState, type LogEntry, type Participant,
} from './game';
import {
  FLIP_S, STARTER_LABEL, WIND_S, cardTitle, readDeck, starterDeck, toSablierDeck,
  type ActionFx, type Card, type JokerFx,
} from './game-cards';
import { BIBS, BIB_HEX, BIB_INK, isBib, type Bib } from './groups';
import type { TeamGender } from './labels';
import { BIB_LETTER, MEMBERS_MAX, TEAMS_MAX, teamName, type ParcoursConfig } from './parcours';

// -----------------------------------------------------------------------------------------------------------------
// Les réglages (games.config, relus sans confiance)
// -----------------------------------------------------------------------------------------------------------------
export type SablierMode = 'same' | 'own';
export const MODES: SablierMode[] = ['same', 'own'];
export const MODE_LABEL: Record<SablierMode, { title: string; sub: string }> = {
  same: { title: 'Même carte pour tous', sub: 'Une carte pour toutes les équipes, en même temps ; la suivante quand toutes ont fini.' },
  own: { title: 'Chacune sa carte', sub: 'Chaque équipe tire la sienne et enchaîne à son rythme.' },
};
export type SablierFail = 'minus20' | 'retry' | 'change';
export const SABLIER_FAILS: SablierFail[] = ['minus20', 'retry', 'change'];
export const SABLIER_FAIL_LABEL: Record<SablierFail, { title: string; sub: string; same: string; short: string }> = {
  minus20: { title: '−20 s et une autre carte', sub: 'puis la carte suivante se retourne', same: 'puis on attend la carte suivante', short: '−20 s' },
  retry: { title: 'Réessaie', sub: 'la même carte, le sable coule', same: 'la même carte, le sable coule', short: 'réessaie' },
  change: { title: 'Une autre carte, sans pénalité', sub: 'le temps passé est déjà perdu', same: '', short: 'autre carte' },
};
/** Les réserves proposées (secondes), 8 min par défaut ; ajustables équipe par équipe, sans plafond. */
export const RESERVES = [300, 480, 720];
export const RESERVE_DEFAULT = 480;
export const RESERVE_STEP = 30;
export const RESERVE_MIN = 30;
export const RESERVE_MAX = 3600;
/** Le temps maximum d'une carte en « Même carte pour tous » (secondes ; 0 : sans limite). */
export const MAXCARDS = [60, 120, 180, 0];
export const MAXCARD_DEFAULT = 120;
export const MISS_MS = 20_000;
export const READ_MS = 5_000;
/** Le temps de fêter un STOP avant que la carte suivante se retourne (« Chacune sa carte »). */
export const GAP_MS = 2_000;
export const GRACE_MS = 500;
export const STOP_MS = 15 * 60_000;
/** La durée annoncée d'une partie, selon la réserve. */
export const aboutMin = (reserve_s: number) => Math.max(3, Math.round(reserve_s / 60 * 1.25));

export interface SablierTeam { bib: Bib; name: string; members: string[]; reserve_s: number }
export interface SablierConfig {
  mode: SablierMode;
  teams: SablierTeam[];
  /** La réserve de départ choisie (la même pour toutes, ajustable ensuite équipe par équipe). */
  reserve_s: number;
  fail: SablierFail;
  /** « Même carte pour tous » : le temps maximum d'une carte (0 : sans limite). */
  maxcard_s: number;
  stop15: boolean;
  sound: boolean;
  /** Les téléphones par QR (D-167) : un par équipe ; celui du coach en télécommande. */
  phones: boolean;
  remote: boolean;
  deckName: string;
  deck: Card[];
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ids = (v: unknown, max: number) => (Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string' && UUID.test(x)))].slice(0, max) : []);
const str = (v: unknown, max: number) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '');
const secs = (v: unknown, def: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(RESERVE_MIN, Math.min(RESERVE_MAX, Math.round(v))) : def);

/** Les réglages d'une partie du Sablier, relus sans confiance. */
export function readSablierConfig(v: unknown, gender: TeamGender = 'X'): SablierConfig {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const reserve_s = secs(o.reserve_s, RESERVE_DEFAULT);
  const used = new Set<Bib>();
  const teams: SablierTeam[] = [];
  for (const t of Array.isArray(o.teams) ? o.teams.slice(0, TEAMS_MAX) : []) {
    if (!t || typeof t !== 'object') continue;
    const x = t as Record<string, unknown>;
    const bib = isBib(x.bib) && !used.has(x.bib) ? x.bib : BIBS.find(b => !used.has(b))!;
    used.add(bib);
    teams.push({ bib, name: str(x.name, 24) || teamName(bib, gender), members: ids(x.members, MEMBERS_MAX), reserve_s: secs(x.reserve_s, reserve_s) });
  }
  const mode: SablierMode = o.mode === 'own' ? 'own' : 'same';
  const deck = readDeck(o.deck);
  let fail: SablierFail = SABLIER_FAILS.includes(o.fail as SablierFail) ? o.fail as SablierFail : 'minus20';
  if (mode === 'same' && fail === 'change') fail = 'minus20';
  return {
    mode, teams, reserve_s, fail,
    maxcard_s: MAXCARDS.includes(o.maxcard_s as number) ? o.maxcard_s as number : MAXCARD_DEFAULT,
    stop15: o.stop15 === true,
    sound: o.sound !== false,
    phones: o.phones !== false,
    remote: o.remote !== false,
    deckName: str(o.deckName, 40) || `Paquet ${STARTER_LABEL.u13}`,
    deck: deck.length ? deck : toSablierDeck(starterDeck('u13')),
  };
}

/** Le Sablier préparé à partir du Parcours (le même exercice, les mêmes équipes, le même paquet converti). */
export function sablierFromParcours(c: ParcoursConfig, o: { reserve_s?: number; mode?: SablierMode; fail?: SablierFail; maxcard_s?: number; sound?: boolean; stop15?: boolean } = {}): SablierConfig {
  const reserve_s = o.reserve_s ?? RESERVE_DEFAULT;
  const mode = o.mode ?? 'same';
  let fail = o.fail ?? 'minus20';
  if (mode === 'same' && fail === 'change') fail = 'minus20';
  return {
    mode, reserve_s, fail, maxcard_s: o.maxcard_s ?? MAXCARD_DEFAULT, stop15: o.stop15 ?? false, sound: o.sound ?? c.sound, phones: c.phones, remote: c.remote,
    teams: c.teams.map(t => ({ ...t, members: [...t.members], reserve_s })),
    deckName: c.deckName, deck: toSablierDeck(c.deck),
  };
}

/** Ce qui manque pour lancer : deux équipes au moins, une carte Défi au moins. */
export function sablierProblem(c: SablierConfig): string | null {
  if (c.teams.length < 2) return 'Il faut deux équipes au moins.';
  if (c.teams.some(t => !t.members.length)) return 'Une équipe est vide : ajoute quelqu’un ou retire-la.';
  if (!c.deck.some(x => x.kind === 'defi')) return 'Le paquet n’a aucun défi.';
  return null;
}

export const sablierParticipants = (c: SablierConfig): Participant[] =>
  c.teams.map((t, i) => ({ id: `t${i}`, name: t.name, color: BIB_HEX[t.bib], ink: BIB_INK[t.bib], letter: BIB_LETTER[t.bib], members: t.members }));

// -----------------------------------------------------------------------------------------------------------------
// L'état de la partie
// -----------------------------------------------------------------------------------------------------------------
export type SPhase = 'defi' | 'wait' | 'out';
export interface SState {
  /** Le sable (ms) au moment `flowFrom` ; arrêté : le sable maintenant. */
  res: number;
  /** Temps de jeu où le sable (re)commence à couler ; dans le futur : la carte se lit ; null : arrêté. */
  flowFrom: number | null;
  phase: SPhase;
  card: Card | null;
  cardIdx: number | null;
  jokers: JokerFx[];
  ok: number;
  ko: number;
  /** Temps passé sur les défis réussis. */
  okMs: number;
  /** Rendu par les cartes ; perdu par les ratés et les sanctions. */
  gained: number;
  lost: number;
  /** La dernière règle appliquée, en une ligne (« Raté · −20 s »). */
  note: string | null;
  noteKo: boolean;
  /** Le dernier STOP : la carte, quand (temps de jeu), en combien, ce qu'il a rendu. */
  last: { title: string; at: number; ms: number; gain: number } | null;
}
export interface Round { card: Card; idx: number; from: number; until: number | null; n: number }
export interface SShared {
  rules: { mode: SablierMode; fail: SablierFail; maxMs: number; readMs: number; deck: Card[] };
  draw: number[];
  used: number[];
  /** Par carte : essais, ratés, sable coûté (ms). */
  cardStats: Record<number, { tries: number; ko: number; ms: number }>;
  /** « Même carte pour tous » : la carte en cours, pour toutes. */
  round: Round | null;
  endedAt: number | null;
  endedBy: 'zero' | 'time' | 'coach' | null;
  zeroBy: string | null;
}
export type SablierState = GameState<SState, SShared>;

/** Ce que l'écran montre : une carte qui se retourne (pour une équipe, ou pour toutes), le STOP, du sable rendu ou pris, le zéro. */
export type SFx =
  | { t: 'card'; who: string | null; card: Card }
  | { t: 'stop'; who: string; ms: number; gain: number; card: Card }
  | { t: 'gain'; who: string; ms: number; why: string }
  | { t: 'loss'; who: string; ms: number; why: string }
  | { t: 'zero'; who: string };
export interface SStep { g: SablierState; fx: SFx[] }

const fresh = (res: number): SState => ({ res, flowFrom: null, phase: 'wait', card: null, cardIdx: null, jokers: [], ok: 0, ko: 0, okMs: 0, gained: 0, lost: 0, note: null, noteKo: false, last: null });

/** Le sable d'une équipe au temps de jeu `t` (peut être négatif : à zéro avant). */
export const sandAt = (p: SState, t: number) => (p.flowFrom === null ? p.res : p.res - Math.max(0, t - p.flowFrom));
/** Le moment (temps de jeu) où son sable touche zéro, s'il coule. */
export const zeroAt = (p: SState) => (p.flowFrom === null || p.phase === 'out' ? null : p.flowFrom + p.res);
/** La carte se lit encore (le sable attend). */
export const reading = (p: SState, t: number) => p.flowFrom !== null && t < p.flowFrom;

const alive = (g: SablierState) => g.participants.filter(p => g.ps[p.id]!.phase !== 'out').map(p => p.id);
export const nameOfS = (g: SablierState, id: string) => g.participants.find(p => p.id === id)?.name ?? '';

/** Tire une carte : la pioche, remélangée avec les cartes jouées quand elle est vide (sauf les cartes en cours). */
function drawIdx(s: SShared, ps: Record<string, SState>, rand: () => number): { idx: number; shared: SShared } {
  let draw = s.draw, used = s.used;
  if (!draw.length) {
    const busy = new Set(Object.values(ps).map(p => p.cardIdx).filter((x): x is number => x !== null));
    if (s.round) busy.add(s.round.idx);
    const back = used.filter(i => !busy.has(i));
    draw = shuffled(back.length ? back : s.rules.deck.map((_, i) => i), rand);
    used = used.filter(i => busy.has(i));
  }
  const idx = draw[draw.length - 1]!;
  return { idx, shared: { ...s, draw: draw.slice(0, -1), used: [...used, idx] } };
}

/** Le sable pris à une équipe au temps `t` (un raté, une sanction, un coup de vent) ; à zéro : elle est dehors. */
function take(p: SState, t: number, ms: number): SState {
  const left = sandAt(p, t) - ms;
  if (left <= 0) return { ...p, res: 0, flowFrom: null, phase: 'out', lost: p.lost + Math.max(0, sandAt(p, t)) };
  return { ...p, res: p.res - ms, lost: p.lost + ms };
}
const give = (p: SState, ms: number): SState => ({ ...p, res: p.res + ms, gained: p.gained + ms });

/** Ce que fait une action tirée au temps `t` par `who` (null : pour toutes, « Même carte pour tous »). */
function applyAction(fx: ActionFx, who: string | null, g: SablierState, ps: Record<string, SState>, t: number, out: SFx[]): void {
  const ids = alive({ ...g, ps });
  if (fx === 'flip' || fx === 'plus2' || fx === 'replay') {
    for (const id of who ? [who] : ids) { ps[id] = give(ps[id]!, FLIP_S * 1000); out.push({ t: 'gain', who: id, ms: FLIP_S * 1000, why: 'Sablier retourné' }); }
    return;
  }
  const rivals = ids.filter(id => id !== who);
  const top = Math.max(...rivals.map(id => sandAt(ps[id]!, t)));
  for (const id of rivals.filter(id => sandAt(ps[id]!, t) === top && top > 0)) {
    ps[id] = { ...take(ps[id]!, t, WIND_S * 1000), note: 'Coup de vent · −15 s', noteKo: true };
    out.push({ t: 'loss', who: id, ms: WIND_S * 1000, why: 'Coup de vent' });
    if (ps[id]!.phase === 'out') out.push({ t: 'zero', who: id });
  }
}

/** Un défi pris au hasard dans le paquet, même déjà en jeu (petit paquet : toutes ses cartes sont en main). */
function anyDefi(deck: Card[], rand: () => number, not: number | null = null): { card: Card; idx: number } | null {
  const list = deck.map((card, idx) => ({ card, idx })).filter(x => x.card.kind === 'defi' && x.idx !== not);
  return list.length ? list[Math.floor(rand() * list.length)]! : null;
}

/**
 * La prochaine carte d'une équipe (« Chacune sa carte ») : on tire jusqu'à un défi ; une action s'applique tout de suite,
 * un joker se garde (une seule de ces cartes entre deux défis). Le sable attend `delay` puis le temps de lire.
 */
function nextCardFor(g: SablierState, ps: Record<string, SState>, shared: SShared, who: string, t: number, delay: number, rand: () => number, out: SFx[]): SShared {
  const deck = shared.rules.deck;
  let extra = 0, acted = false;
  const take1 = (card: Card, idx: number) => {
    out.push({ t: 'card', who, card });
    const p = ps[who]!;
    ps[who] = { ...p, phase: 'defi', card, cardIdx: idx, res: Math.max(0, sandAt(p, t)), flowFrom: t + delay + extra + shared.rules.readMs };
  };
  for (let i = 0; i <= deck.length; i++) {
    const dr = drawIdx(shared, ps, rand);
    shared = dr.shared;
    const card = deck[dr.idx]!;
    if (card.kind === 'defi') { take1(card, dr.idx); return shared; }
    if (acted) continue;
    acted = true;
    out.push({ t: 'card', who, card });
    if (card.kind === 'joker') ps[who] = { ...ps[who]!, jokers: [...ps[who]!.jokers, card.fx as JokerFx] };
    else applyAction(card.fx as ActionFx, who, g, ps, t, out);
    if (ps[who]!.phase === 'out') return shared;
    extra += 2_500;   // la carte d'action se montre, puis la suivante
  }
  const x = anyDefi(deck, rand);
  if (x) take1(x.card, x.idx);
  return shared;
}

/** Une nouvelle carte pour toutes (« Même carte pour tous ») : on tire jusqu'à un défi ; une action vaut pour toutes. */
function nextRound(g: SablierState, ps: Record<string, SState>, shared: SShared, t: number, delay: number, rand: () => number, out: SFx[]): SShared {
  const deck = shared.rules.deck;
  let extra = 0, acted = false;
  const prev = shared.round;
  const open = (card: Card, idx: number): SShared => {
    out.push({ t: 'card', who: null, card });
    const from = t + delay + extra + shared.rules.readMs;
    const round: Round = { card, idx, from, until: shared.rules.maxMs ? from + shared.rules.maxMs : null, n: (prev?.n ?? 0) + 1 };
    for (const id of alive({ ...g, ps })) ps[id] = { ...ps[id]!, phase: 'defi', card, cardIdx: idx, res: Math.max(0, sandAt(ps[id]!, t)), flowFrom: from, note: null, noteKo: false };
    return { ...shared, round };
  };
  for (let i = 0; i <= deck.length; i++) {
    const dr = drawIdx({ ...shared, round: null }, Object.fromEntries(Object.entries(ps).map(([id, p]) => [id, { ...p, cardIdx: null }])), rand);
    shared = { ...dr.shared, round: prev };
    const card = deck[dr.idx]!;
    if (card.kind === 'defi' && dr.idx !== prev?.idx) return open(card, dr.idx);
    if (card.kind !== 'action' || acted) continue;   // les jokers ne sortent pas en « Même carte pour tous »
    acted = true;
    out.push({ t: 'card', who: null, card });
    applyAction(card.fx as ActionFx, null, g, ps, t, out);
    if (anyOut(ps)) return shared;
    extra += 2_500;
  }
  const x = anyDefi(deck, rand, prev?.idx ?? null) ?? anyDefi(deck, rand);
  return x ? open(x.card, x.idx) : shared;
}

const anyOut = (ps: Record<string, SState>) => Object.entries(ps).find(([, p]) => p.phase === 'out')?.[0] ?? null;
/** Une équipe vidée par un geste (un raté, une sanction, un coup de vent) : la partie s'arrête à cet instant. */
function endIfOut(shared: SShared, ps: Record<string, SState>, t: number): SShared {
  const out = anyOut(ps);
  if (!out || shared.endedBy) return shared;
  for (const [id, p] of Object.entries(ps)) if (p.phase !== 'out' && p.flowFrom !== null) ps[id] = { ...p, res: Math.max(0, sandAt(p, t)), flowFrom: null };
  return { ...shared, endedAt: t, endedBy: 'zero', zeroBy: out };
}

// -----------------------------------------------------------------------------------------------------------------
// La partie
// -----------------------------------------------------------------------------------------------------------------
export function newSablier(c: SablierConfig, participants: Participant[], rand: () => number, now: number): SStep {
  const deck = c.deck.length ? c.deck : toSablierDeck(starterDeck('u13'));
  const mode = c.mode;
  const shared0: SShared = {
    rules: { mode, fail: mode === 'same' && c.fail === 'change' ? 'minus20' : c.fail, maxMs: (c.maxcard_s || 0) * 1000, readMs: READ_MS, deck },
    draw: shuffled(deck.map((_, i) => i), rand), used: [], cardStats: {}, round: null, endedAt: null, endedBy: null, zeroBy: null,
  };
  const g0 = createGame<SState, SShared>('teams', participants, p => fresh((c.teams[Number(p.id.slice(1))]?.reserve_s ?? c.reserve_s) * 1000), shared0, now);
  const ps: Record<string, SState> = { ...g0.ps };
  const fx: SFx[] = [];
  let shared = shared0;
  if (mode === 'same') shared = nextRound(g0, ps, shared, 0, 0, rand, fx);
  else for (const p of participants) shared = nextCardFor(g0, ps, shared, p.id, 0, 0, rand, fx);
  return { g: { ...g0, ps, shared }, fx };
}

const statsAdd = (s: SShared, idx: number | null, d: { tries?: number; ko?: number; ms?: number }): SShared => {
  if (idx === null) return s;
  const cur = s.cardStats[idx] ?? { tries: 0, ko: 0, ms: 0 };
  return { ...s, cardStats: { ...s.cardStats, [idx]: { tries: cur.tries + (d.tries ?? 0), ko: cur.ko + (d.ko ?? 0), ms: cur.ms + Math.max(0, d.ms ?? 0) } } };
};
const fmt = (ms: number) => { const s = Math.round(Math.max(0, ms) / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

/** STOP (« c'est fait ») : le sable s'arrête ; un défi difficile rend son temps ; « Chacune sa carte » : la suivante se retourne. */
export function stop(g: SablierState, who: string, rand: () => number, now: number): SStep {
  const p = g.ps[who];
  if (!p || p.phase !== 'defi' || !p.card || p.flowFrom === null || g.over || g.clock.pausedAt !== null) return { g, fx: [] };
  const t = gameMs(g, now);
  if (t < p.flowFrom) return { g, fx: [] };   // la carte se lit encore
  if (t > p.flowFrom + p.res + GRACE_MS) return { g, fx: [] };   // trop tard : le zéro est passé
  const ms = t - p.flowFrom;
  const gain = (p.card.sec ?? 0) * 1000;
  const card = p.card;
  const fx: SFx[] = [{ t: 'stop', who, ms, gain, card }];
  if (gain) fx.push({ t: 'gain', who, ms: gain, why: cardTitle(card) });
  let shared = statsAdd(g.shared, p.cardIdx, { tries: 1, ms });
  const all: Record<string, SState> = { ...g.ps };
  all[who] = { ...p, res: Math.max(0, p.res - ms) + gain, flowFrom: null, phase: 'wait', ok: p.ok + 1, okMs: p.okMs + ms, gained: p.gained + gain,
    note: gain ? `Stop · fait en ${fmt(ms)} · +${gain / 1000} s` : `Stop · fait en ${fmt(ms)}`, noteKo: false, last: { title: cardTitle(card), at: t, ms, gain } };
  if (shared.rules.mode === 'own') shared = nextCardFor(g, all, shared, who, t, GAP_MS, rand, fx);
  shared = endIfOut(shared, all, t);
  const next = act(g, now, { who, kind: 'stop', text: `STOP ${cardTitle(card)} en ${fmt(ms)}${gain ? `, +${gain / 1000} s` : ''}`, declared: true },
    { ps: changed(g, all), shared, over: shared.endedBy !== null || undefined });
  return { g: next, fx };
}
/** Les équipes dont l'état a changé (ce que le journal garde). */
const changed = (g: SablierState, all: Record<string, SState>) => Object.fromEntries(Object.entries(all).filter(([id, x]) => x !== g.ps[id]));

/** « Raté » : la règle de la partie (−20 s et une autre carte par défaut) ; le joker « annule un raté » l'efface. */
export function fail(g: SablierState, who: string, rand: () => number, now: number, opts: { joker?: boolean } = {}): SStep {
  const p = g.ps[who];
  if (!p || p.phase !== 'defi' || !p.card || p.flowFrom === null || g.over || g.clock.pausedAt !== null) return { g, fx: [] };
  const t = gameMs(g, now);
  if (t < p.flowFrom) return { g, fx: [] };
  if (t > p.flowFrom + p.res + GRACE_MS) return { g, fx: [] };
  const rule = g.shared.rules.fail;
  const same = g.shared.rules.mode === 'same';
  const card = p.card;
  const ms = t - p.flowFrom;
  const sand = Math.max(0, sandAt(p, t));
  const fx: SFx[] = [];
  let shared = g.shared;
  const all: Record<string, SState> = { ...g.ps };
  let tail: string;
  if (opts.joker && p.jokers.includes('cancel')) {
    // Le joker : le raté ne compte pas ; on passe à la suite sans pénalité.
    const k = p.jokers.indexOf('cancel');
    shared = statsAdd(shared, p.cardIdx, { tries: 1, ms });
    all[who] = { ...p, jokers: p.jokers.filter((_, i) => i !== k), res: sand, flowFrom: null, phase: 'wait', note: 'Raté · joker : rien ne change', noteKo: false };
    if (!same) shared = nextCardFor(g, all, shared, who, t, GAP_MS, rand, fx);
    tail = ' : joker « annule un raté »';
  } else if (rule === 'retry') {
    shared = statsAdd(shared, p.cardIdx, { ko: 1 });
    all[who] = { ...p, ko: p.ko + 1, note: 'Raté · réessaie', noteKo: true };
    tail = ' : réessaie';
  } else if (rule === 'change' && !same) {
    shared = statsAdd(shared, p.cardIdx, { tries: 1, ko: 1, ms });
    all[who] = { ...p, ko: p.ko + 1, res: sand, flowFrom: null, phase: 'wait', note: 'Raté · une autre carte', noteKo: true };
    shared = nextCardFor(g, all, shared, who, t, 0, rand, fx);
    tail = ' : une autre carte';
  } else {
    shared = statsAdd(shared, p.cardIdx, { tries: 1, ko: 1, ms: ms + Math.min(MISS_MS, sand) });
    const np = take({ ...p, res: sand, flowFrom: null }, t, MISS_MS);
    fx.push({ t: 'loss', who, ms: MISS_MS, why: 'Raté' });
    if (np.phase === 'out') { all[who] = { ...np, ko: p.ko + 1, note: 'Raté · plus de sable', noteKo: true }; fx.push({ t: 'zero', who }); }
    else {
      all[who] = { ...np, ko: p.ko + 1, phase: 'wait', note: 'Raté · −20 s', noteKo: true };
      if (!same) shared = nextCardFor(g, all, shared, who, t, GAP_MS, rand, fx);
    }
    tail = ' : −20 s';
  }
  shared = endIfOut(shared, all, t);
  const next = act(g, now, { who, kind: 'fail', text: `« Raté » ${cardTitle(card)}${tail}` }, { ps: changed(g, all), shared, over: shared.endedBy !== null || undefined });
  return { g: next, fx };
}

/** Le joker « Change de défi » : une autre carte, sans pénalité, le temps de la lire. */
export function jokerChange(g: SablierState, who: string, rand: () => number, now: number): SStep {
  const p = g.ps[who];
  if (!p || p.phase !== 'defi' || !p.jokers.includes('change') || g.over || g.clock.pausedAt !== null) return { g, fx: [] };
  const t = gameMs(g, now);
  const k = p.jokers.indexOf('change');
  const r = swapCard(g, who, t, rand);
  if (!r) return { g, fx: [] };
  const np: SState = { ...r.p, jokers: p.jokers.filter((_, i) => i !== k), note: 'Joker · change de défi', noteKo: false };
  const next = act(g, now, { who, kind: 'joker', text: `joker « change de défi » : ${cardTitle(r.p.card!)}` }, { ps: { [who]: np }, shared: r.shared });
  return { g: next, fx: [{ t: 'card', who, card: r.p.card! }] };
}

/** Une autre carte Défi pour une équipe (joker, coach) : le sable s'arrête le temps de la lire. */
function swapCard(g: SablierState, who: string, t: number, rand: () => number): { p: SState; shared: SShared } | null {
  const p = g.ps[who]!;
  let shared = g.shared;
  const to = (c: Card, idx: number) => ({ p: { ...p, card: c, cardIdx: idx, res: Math.max(0, sandAt(p, t)), flowFrom: t + shared.rules.readMs, phase: 'defi' as const }, shared });
  for (let i = 0; i <= shared.rules.deck.length; i++) {
    const dr = drawIdx(shared, g.ps, rand);
    shared = dr.shared;
    const c = shared.rules.deck[dr.idx]!;
    if (c.kind === 'defi' && dr.idx !== p.cardIdx) return to(c, dr.idx);
  }
  const x = anyDefi(shared.rules.deck, rand, p.cardIdx);
  return x ? to(x.card, x.idx) : null;
}

// -----------------------------------------------------------------------------------------------------------------
// Le temps qui passe : le zéro (à l'heure de la base, 0,5 s de grâce), la fin d'une carte pour toutes, 15 min
// -----------------------------------------------------------------------------------------------------------------
/** La prochaine échéance (temps de jeu) : un zéro, la fin du temps d'une carte, la fin des 15 min ; pour l'horloge de l'écran. */
export function nextDeadline(g: SablierState, stop15: boolean): number | null {
  if (g.over) return null;
  const zs = g.participants.map(p => zeroAt(g.ps[p.id]!)).filter((x): x is number => x !== null).map(z => z + GRACE_MS);
  const r = g.shared.round?.until ?? null;
  return Math.min(...zs, r ?? Infinity, stop15 ? STOP_MS : Infinity);
}

/**
 * L'horloge : à `now`, ce qui doit arriver arrive, dans l'ordre. Un zéro passé de plus de 0,5 s arrête la partie à
 * l'instant du zéro (ce que les autres ont fait après est défait) ; « Même carte pour tous » : quand toutes ont fini, ou au
 * bout du temps maximum, la carte suivante ; « Arrêter à 15 min ».
 */
export function tick(g: SablierState, now: number, rand: () => number, stop15 = false): SStep {
  const fx: SFx[] = [];
  for (let guard = 0; guard < 12 && !g.over && g.clock.pausedAt === null; guard++) {
    const t = gameMs(g, now);
    const z = g.participants.map(p => ({ id: p.id, z: zeroAt(g.ps[p.id]!) })).filter((x): x is { id: string; z: number } => x.z !== null).sort((a, b) => a.z - b.z)[0];
    const round = g.shared.round;
    const zNext = z?.z ?? Infinity;
    const uNext = g.shared.rules.mode === 'same' ? round?.until ?? Infinity : Infinity;
    const sNext = stop15 ? STOP_MS : Infinity;
    if (z && zNext <= uNext && zNext <= sNext) {
      // Le premier zéro : la partie s'arrête à cet instant, une fois la grâce passée (un STOP peut encore arriver).
      if (zNext + GRACE_MS <= t) { g = endAtZero(g, z.id, zNext, now); fx.push({ t: 'zero', who: z.id }); }
      break;
    }
    if (sNext <= uNext && t >= sNext) { g = endGame(g, 'time', now, STOP_MS); break; }
    if (g.shared.rules.mode !== 'same' || !round) break;
    const pending = alive(g).filter(id => g.ps[id]!.phase === 'defi');
    if (pending.length && t >= uNext) {
      // Le temps de la carte est passé : celles qui n'ont pas fini s'arrêtent là (sans raté), puis la carte suivante.
      const ps: Record<string, SState> = {};
      let shared = g.shared;
      for (const id of pending) {
        const p = g.ps[id]!;
        ps[id] = { ...p, res: Math.max(0, sandAt(p, uNext)), flowFrom: null, phase: 'wait', note: 'Temps écoulé', noteKo: true };
        shared = statsAdd(shared, p.cardIdx, { tries: 1, ms: Math.max(0, uNext - (p.flowFrom ?? uNext)) });
      }
      g = act(g, now, { who: null, kind: 'timeout', text: `temps écoulé sur ${cardTitle(round.card)}` }, { ps, shared });
      continue;
    }
    if (!pending.length) {
      // Toutes ont fini : la carte suivante, pour toutes.
      const all: Record<string, SState> = { ...g.ps };
      let shared = nextRound(g, all, g.shared, t, GAP_MS, rand, fx);
      shared = endIfOut(shared, all, t);
      g = act(g, now, { who: null, kind: 'round', text: `carte suivante pour toutes : ${shared.round ? cardTitle(shared.round.card) : ''}` },
        { ps: changed(g, all), shared, over: shared.endedBy !== null || undefined });
      continue;
    }
    break;
  }
  return { g, fx };
}

/**
 * Le zéro d'une équipe à l'instant `z` (temps de jeu) : la partie s'arrête là pour tout le monde. Ce qui a été fait après
 * cet instant (un STOP arrivé plus tard) est défait, puis chaque sablier se fige à sa valeur de l'instant `z`.
 */
export function endAtZero(g: SablierState, who: string, z: number, now: number): SablierState {
  let ps = { ...g.ps };
  let shared = g.shared;
  const log = [...g.log];
  for (let i = log.length - 1; i >= 0; i--) {
    const e = log[i]!;
    if (e.at <= z) break;
    if (e.undone || e.arbitration) continue;
    ps = { ...ps, ...e.before.ps };
    if (e.before.shared !== undefined) shared = e.before.shared;
    log[i] = { ...e, undone: 'invalid' } as LogEntry<SState, SShared>;
  }
  for (const [id, p] of Object.entries(ps)) {
    ps[id] = id === who ? { ...p, res: 0, flowFrom: null, phase: 'out', note: 'À zéro', noteKo: true }
      : { ...p, res: Math.max(0, sandAt(p, z)), flowFrom: null };
  }
  const restored: SablierState = { ...g, log, ps: g.ps };
  return act(restored, now, { who: null, kind: 'zero', text: `${nameOfS(g, who)} à zéro : la partie s'arrête`, arbitration: true },
    { ps, shared: { ...shared, endedAt: z, endedBy: 'zero', zeroBy: who }, over: true });
}

/** La fin sans zéro : « Arrêter à 15 min », ou le coach arrête ; chaque sablier se fige. */
export function endGame(g: SablierState, by: 'time' | 'coach', now: number, at?: number): SablierState {
  if (g.over) return g;
  const t = at ?? gameMs(g, now);
  const ps: Record<string, SState> = {};
  for (const [id, p] of Object.entries(g.ps)) ps[id] = { ...p, res: Math.max(0, sandAt(p, t)), flowFrom: null };
  return act(g, now, { who: null, kind: 'end', text: by === 'time' ? 'fin du temps (15 min)' : 'partie arrêtée par le coach', arbitration: true },
    { ps, shared: { ...g.shared, endedAt: t, endedBy: by }, over: true });
}

// -----------------------------------------------------------------------------------------------------------------
// L'arbitrage du coach (sur la tablette avec le code, ou au téléphone du coach sans code)
// -----------------------------------------------------------------------------------------------------------------
export type SSanction = 15 | 30;
export const S_SANCTIONS: SSanction[] = [15, 30];

export function sanction(g: SablierState, who: string, s: SSanction, now: number): SStep {
  const p = g.ps[who];
  if (!p || g.over || p.phase === 'out') return { g, fx: [] };
  const t = gameMs(g, now);
  const all: Record<string, SState> = { ...g.ps, [who]: { ...take(p, t, s * 1000), note: `Sanction · −${s} s`, noteKo: true } };
  const shared = endIfOut(g.shared, all, t);
  const ps = Object.fromEntries(Object.entries(all).filter(([id, x]) => x !== g.ps[id]));
  const next = act(g, now, { who, kind: 'sanction', text: `sanction : −${s} s`, coach: true }, { ps, shared, over: shared.endedBy !== null || undefined });
  const fx: SFx[] = [{ t: 'loss', who, ms: s * 1000, why: 'Sanction' }];
  if (shared.endedBy) fx.push({ t: 'zero', who });
  return { g: next, fx };
}

/** Le dernier STOP de l'équipe encore valable : ce que « Invalider ce STOP » défait. */
export function lastStop(g: SablierState, who: string): { title: string; at: number; ms: number; gain: number } | null {
  return lastDeclared(g, who, ['stop']) ? g.ps[who]?.last ?? null : null;
}

/**
 * « Invalider ce STOP » : la carte revient, le sable recoule depuis maintenant (sans ce qu'elle avait rendu). Si la carte
 * suivante est déjà tirée pour toutes (« Même carte pour tous »), seul ce qu'elle avait rendu est repris.
 */
export function invalidate(g: SablierState, who: string, now: number): SStep {
  const e = lastDeclared(g, who, ['stop']);
  const before = e?.before.ps[who];
  const p = g.ps[who];
  if (!e || !before || !p || g.over || p.phase === 'out') return { g, fx: [] };
  const t = gameMs(g, now);
  const at = e.at;
  const sandThen = Math.max(0, sandAt(before, at));
  const gain = p.last?.gain ?? 0;
  const log = g.log.map(x => (x.seq === e.seq ? { ...x, undone: 'invalid' as const } : x));
  const same = g.shared.rules.mode === 'same';
  const sameRound = same && g.shared.round && before.cardIdx === g.shared.round.idx && p.phase === 'wait';
  let np: SState;
  let shared = g.shared;
  if (same && !sameRound) {
    np = { ...take(p, t, gain), ok: Math.max(0, p.ok - 1), gained: Math.max(0, p.gained - gain), note: 'STOP invalidé par le coach', noteKo: true };
  } else {
    // La carte revient : celle tirée après retourne dans la pioche.
    if (!same && p.cardIdx !== null && p.cardIdx !== before.cardIdx) shared = { ...shared, draw: [...shared.draw, p.cardIdx], used: shared.used.filter(i => i !== p.cardIdx) };
    np = { ...p, card: before.card, cardIdx: before.cardIdx, phase: 'defi', res: sandThen, flowFrom: t, ok: Math.max(0, p.ok - 1), okMs: Math.max(0, p.okMs - (p.last?.ms ?? 0)),
      gained: Math.max(0, p.gained - gain), note: 'STOP invalidé par le coach', noteKo: true, last: null };
    const st = before.cardIdx !== null ? shared.cardStats[before.cardIdx] : undefined;
    if (st && before.cardIdx !== null) shared = { ...shared, cardStats: { ...shared.cardStats, [before.cardIdx]: { ...st, tries: Math.max(0, st.tries - 1) } } };
  }
  const all = { ...g.ps, [who]: np };
  shared = endIfOut(shared, all, t);
  const next = act({ ...g, log }, now, { who: null, kind: 'invalidate', text: `invalide le STOP de ${nameOfS(g, who)} (« ${cardTitle(before.card ?? p.card!)} »)`, arbitration: true },
    { ps: shared.endedBy ? all : { [who]: np }, shared, over: shared.endedBy !== null || undefined });
  return { g: next, fx: [] };
}

/** « Changer leur carte » (coach) : carte injouable (matériel, blessure), une autre sans pénalité. */
export function changeCard(g: SablierState, who: string, rand: () => number, now: number): SStep {
  const p = g.ps[who];
  if (!p || g.over || p.phase !== 'defi') return { g, fx: [] };
  const t = gameMs(g, now);
  const r = swapCard(g, who, t, rand);
  if (!r) return { g, fx: [] };
  const next = act(g, now, { who, kind: 'change', text: `carte changée par le coach : ${cardTitle(r.p.card!)}`, coach: true },
    { ps: { [who]: { ...r.p, note: 'Carte changée par le coach', noteKo: false } }, shared: r.shared });
  return { g: next, fx: [{ t: 'card', who, card: r.p.card! }] };
}

export function lastActionText(g: SablierState): { text: string; at: number } | null {
  const e = lastUndoable(g);
  return e ? { text: `${e.who ? `${nameOfS(g, e.who)} · ` : ''}${e.text}`, at: e.at } : null;
}
export const undo = (g: SablierState, now: number): SablierState => {
  const e = lastUndoable(g);
  return e ? undoLast(g, now, `annule : ${e.who ? `${nameOfS(g, e.who)}, ` : ''}${e.text}`) : g;
};
export const pause = (g: SablierState, on: boolean, now: number) => setPaused(g, on, now);

// -----------------------------------------------------------------------------------------------------------------
// La fin et le bilan du coach
// -----------------------------------------------------------------------------------------------------------------
/** Le sable de chaque équipe, figé à la fin (ou maintenant). */
export const sandNow = (g: SablierState, id: string, now: number) => Math.max(0, sandAt(g.ps[id]!, g.shared.endedAt ?? gameMs(g, now)));

export interface SRank { id: string; rank: number; sand: number; out: boolean }
/** Le classement : le plus de sable gagne ; l'équipe à zéro est dernière ; à égalité, même place. */
export function sRanking(g: SablierState, now: number): SRank[] {
  const list = g.participants.map(p => ({ id: p.id, sand: sandNow(g, p.id, now), out: g.ps[p.id]!.phase === 'out' }))
    .sort((a, b) => Number(a.out) - Number(b.out) || b.sand - a.sand);
  let rank = 0;
  return list.map((x, i) => {
    const prev = list[i - 1];
    if (!prev || prev.out !== x.out || Math.round(prev.sand / 1000) !== Math.round(x.sand / 1000)) rank = i + 1;
    return { ...x, rank };
  });
}

export interface SBilan {
  rows: { id: string; ok: number; ko: number; avgMs: number | null; gained: number }[];
  /** Les cartes qui ont coûté le plus de sable (en moyenne par essai). */
  costly: { card: Card; avgMs: number; ko: number; tries: number }[];
  /** L'équipe qui a perdu le plus sur ses ratés. */
  misses: { id: string; ms: number; ko: number } | null;
}
export function sBilan(g: SablierState): SBilan {
  const rows = g.participants.map(p => { const s = g.ps[p.id]!; return { id: p.id, ok: s.ok, ko: s.ko, avgMs: s.ok ? Math.round(s.okMs / s.ok) : null, gained: s.gained }; });
  const deck = g.shared.rules.deck;
  const costly = Object.entries(g.shared.cardStats).map(([i, s]) => ({ card: deck[Number(i)]!, avgMs: s.tries ? Math.round(s.ms / s.tries) : 0, ko: s.ko, tries: s.tries }))
    .filter(x => x.card && x.tries).sort((a, b) => b.avgMs - a.avgMs).slice(0, 3);
  const worst = g.participants.map(p => ({ id: p.id, ko: g.ps[p.id]!.ko, ms: g.ps[p.id]!.ko * MISS_MS })).filter(x => x.ko >= 2).sort((a, b) => b.ko - a.ko)[0] ?? null;
  return { rows, costly, misses: g.shared.rules.fail === 'minus20' ? worst : null };
}

/** Ce qu'on garde d'une partie jouée (D-103) : sa durée et, par équipe, la chasuble, la place, le sable restant, les défis. */
export function sRunResult(g: SablierState, c: SablierConfig, now: number): { duration_s: number; result: { bib: Bib; rank: number; sand: number; ok: number; ko: number }[] } {
  const duration_s = Math.round((g.shared.endedAt ?? gameMs(g, now)) / 1000);
  const r = new Map(sRanking(g, now).map(x => [x.id, x]));
  return {
    duration_s,
    result: g.participants.map((p, i) => ({ bib: c.teams[i]!.bib, rank: r.get(p.id)!.rank, sand: Math.round(r.get(p.id)!.sand / 1000), ok: g.ps[p.id]!.ok, ko: g.ps[p.id]!.ko })),
  };
}
